import { isDailyRashifalHubPath } from "../../../edge-db/shared/rashifal-freshness";
import { canonicalItemUrl } from "../../../shared/canonical-url";
import { pageBreadcrumbTrail, resolveTrail } from "../breadcrumbs";
import { APP_AVAILABLE } from "../app-availability";
import {
  CONTENT_REVIEWER_ID,
  activeContentReviewer,
  boundReviewEvidence,
  type ReviewEvidence,
} from "../content-reviewer";
import { PAGE_FRESHNESS } from "../page-freshness.generated";
import { freshnessKey } from "../../../edge-db/shared/page-freshness-key";
import { ORGANIZATION_LOGO, SITE_NAME, SITE_URL } from "./site";
import { BUSINESS_POSTAL_ADDRESS } from "../business-address";
import { detectRegionalLocale, regionalLocaleLang } from "../../i18n/regional-locales";

// App store anchors are entity-graph SAME_AS links (rule 26) — only advertise
// them when the app is publicly installable, else they 404 and weaken trust.
const APP_STORE_SAME_AS = [
  "https://play.google.com/store/apps/details?id=com.ishvaram.app",
  "https://apps.apple.com/app/id6743459479",
];

// 2026-07-27: the Wikidata anchor was REMOVED — Q139596352 was deleted on
// 2026-07-20 by an admin via Requests for Deletions ("Does not meet the
// notability policy"). Rule 26 forbids a SAME_AS that does not resolve: a
// crawler follows it, so a dead anchor is worse than an absent one. Re-add only
// once an entity exists again AND `sync_wikidata_to_schema.py` exits 0 — and
// per rule 26, sequence recreation AFTER press coverage or it fails WD:N again.
//
// Replaced by two owner-confirmed, resolving anchors (LinkedIn + Pinterest), so
// the entity graph is honestly above the G100 >=5 threshold rather than propped
// up by a 404. NOTE: an `x.com/ishvaram` account exists and is titled
// "Ishvaram" but is NOT ours (owner-confirmed 2026-07-27) — never add it. That
// is why an anchor needs owner confirmation, not just an HTTP 200.
//
// 2026-10-07: the Facebook Page anchor (facebook.com/ishvaram.in) was REMOVED.
// It showed "This content isn't available" on 2026-10-06 and serves crawlers
// Facebook's no-og:title tombstone, the same shape as a slug that never
// existed. It was in the homepage Organization JSON-LD and, via site-chrome,
// in every EN and HI footer. The liveness guard missed it because the crawler
// UA got HTTP 200; it now reads that tombstone as GONE. Do not swap in the
// other "Ishvaram" profile (profile.php id 61564170023899) until the owner
// confirms it: it still describes a spiritual-products shop.
export const SAME_AS: string[] = [
  ...(APP_AVAILABLE ? APP_STORE_SAME_AS : []),
  // 2026-09-24: the site's own URL was listed here. `sameAs` names OTHER
  // profiles of the entity; pointing it at itself is a self-reference, not an
  // anchor. Every entry below is an owner-confirmed external profile.
  "https://www.instagram.com/ishvaram.in/",
  "https://www.youtube.com/channel/UCmLGFR2IpfqgoWaHuD-L9Vg",
  "https://www.linkedin.com/company/ishvaram/",
  "https://www.pinterest.com/ishvaramindia/",
  // Owner-confirmed 2026-07-30 (owner set the org avatar; we operate the org):
  // public org profile with README, populated the same day (rule 26).
  "https://github.com/Ishvaram-Business",
  // Owner-created + confirmed 2026-07-30. NOTE for the liveness guard: Crunchbase
  // 403s datacenter probes (bot wall) — that is the documented INCONCLUSIVE
  // class, never a dead-anchor alarm (rule 26).
  "https://www.crunchbase.com/organization/ishvaram",
  // Google Business Profile CID link (owner-confirmed 2026-07-30: derived from
  // the owner's live Maps place URL, KG mid /g/11xf9bmq8p; the dashboard
  // "business id" is a DIFFERENT namespace — never anchor that). Maps serves a
  // JS shell to datacenter probes: liveness-guard INCONCLUSIVE class (rule 26).
  "https://maps.google.com/?cid=10448390172209870661",
];

/**
 * The languages the site publishes in (`/` and `/hi/`); see `organizationSchema`.
 * @public scripts/check-google-seo-standards.mjs matches this exact export line.
 */
export const SITE_LANGUAGES = ["en", "hi"];

/**
 * Stable @id for the founder Person node (`Organization.founder`).
 * @internal
 */
export const FOUNDER_ID = `${SITE_URL}#founder`;

/**
 * The real founder (owner-confirmed 2026-07-23), who builds the tools and
 * runs the site. Every field is a fact already public on-site: the name and
 * founder role from this Organization node, "operated by Digesh Nanda" on
 * /terms/ and /contact/, and /about/ as the on-site profile. Deliberately NO
 * `sameAs`, NO `jobTitle`, NO `knowsAbout` and NO astrology credential: the
 * credentialed Jyotish reviewer is a different, real person
 * (`content-reviewer.ts`), and the two must never blur (ADR-0022).
 *
 * 2026-09-23: he is no longer the Article `author`. Pages here are computed by
 * the engine and explained by AI-drafted, rule-reviewed copy (the site's own
 * /editorial-policy/), so naming a person as the author of ~3,000 templated
 * pages was a false byline. `ORG_AUTHOR` is the author; this node stays as the
 * Organization's `founder`, which is true.
 * @internal
 */
export const FOUNDER = {
  "@type": "Person",
  "@id": FOUNDER_ID,
  name: "Digesh Nanda",
  url: `${SITE_URL}/about/`,
} as const;

/** Stable @id for the Organization node the layout graph defines. */
export const ORG_ID = `${SITE_URL}#org`;

/**
 * The Article / WebPage `author`: the Organization that publishes the page.
 * Emitted as a small typed node (not a bare `@id` reference) so it defines
 * `#org` on the page itself and never dangles where the layout graph is absent
 * (worker/static emitters); it merges with the full `ORG` node by `@id`.
 */
export const ORG_AUTHOR = {
  "@type": "Organization",
  "@id": ORG_ID,
  name: SITE_NAME,
  url: SITE_URL,
} as const;

/** @internal */
export const ORG = {
  "@type": "Organization",
  "@id": `${SITE_URL}#org`,
  name: SITE_NAME,
  // The spelling people also search for the brand.
  alternateName: ["Ishwaram"],
  url: SITE_URL,
  address: BUSINESS_POSTAL_ADDRESS,
  logo: ORGANIZATION_LOGO,
  // E-E-A-T: the entity's demonstrated expertise domain + its editorial/methodology
  // page (how facts are computed, what the AI never does). Honest, org-level — no
  // fabricated human (content-reviewer.ts stays owner-gated).
  knowsAbout: [
    "Vedic astrology",
    "Jyotish",
    "Kundali",
    "Panchang",
    "Muhurat",
    "Kundli Matching",
    "Dosha analysis",
    "Nakshatra",
    "Vimshottari Dasha",
    "Sade Sati",
    "Kaal Sarp Dosha",
    "Government Job Yoga",
    "Love Marriage Astrology",
    "Ashtakvarga",
    "Shodashvarga Divisional Charts",
    "Swiss Ephemeris Calculations",
  ],
  publishingPrinciples: `${SITE_URL}/about/`,
  // E-E-A-T contact signal (YMYL): a reachable support channel is a documented
  // quality-rater expectation. Email only — the same address already public on
  // /contact/; no fabricated NAP.
  contactPoint: {
    "@type": "ContactPoint",
    contactType: "customer support",
    email: "support@ishvaram.com",
    availableLanguage: ["en", "hi"],
  },
  // One public, business-wide policy for merchant listings. This mirrors the
  // visible /refund-policy/ terms; reason-specific fees distinguish a normal
  // change-of-mind return from a damaged or incorrect delivery.
  hasMerchantReturnPolicy: {
    "@type": "MerchantReturnPolicy",
    merchantReturnLink: `${SITE_URL}/refund-policy/`,
    applicableCountry: "IN",
    returnPolicyCountry: "IN",
    returnPolicyCategory: "https://schema.org/MerchantReturnFiniteReturnWindow",
    merchantReturnDays: 7,
    returnMethod: "https://schema.org/ReturnByMail",
    returnFees: "https://schema.org/ReturnFeesCustomerResponsibility",
    customerRemorseReturnFees: "https://schema.org/ReturnFeesCustomerResponsibility",
    itemDefectReturnFees: "https://schema.org/FreeReturn",
    refundType: "https://schema.org/FullRefund",
  },
  // Real founder + founding year (owner-confirmed 2026-07-23).
  founder: FOUNDER,
  foundingDate: "2025",
  ...(SAME_AS.length > 0 && { sameAs: SAME_AS }),
} as const;

export function organizationSchema() {
  return {
    "@context": "https://schema.org",
    ...ORG,
    description:
      "Vedic astrology tools powered by Swiss Ephemeris — free kundali, rashifal, dosha analysis, muhurat, and AI-guided Jyotish consultations.",
    areaServed: "IN",
    // The site publishes in two languages (`/` and `/hi/`). The old ten-language
    // list claimed Sanskrit (no edition at all) and seven regional languages
    // whose editions are a handful of noindex pages, not a served site
    // (2026-09-24).
    knowsLanguage: SITE_LANGUAGES,
  };
}

export interface BreadcrumbEntry {
  name: string;
  url: string;
}

/** BreadcrumbList over an already-resolved trail, every item canonicalized. */
export function breadcrumbListSchema(trail: BreadcrumbEntry[]) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: trail.map((breadcrumb, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: breadcrumb.name,
      item: canonicalItemUrl(
        breadcrumb.url.startsWith("http") ? breadcrumb.url : `${SITE_URL}${breadcrumb.url}`
      ),
    })),
  };
}

/**
 * The structured trail for a page path, from the SAME resolver the visible
 * breadcrumb renders (`AutoBreadcrumbs` → `resolveTrail`). The last item is
 * the page itself.
 */
export function pathBreadcrumbEntries(pathname: string, leafName?: string): BreadcrumbEntry[] {
  const path = pathname.replace(/^https?:\/\/[^/]+/, "");
  const trail = pageBreadcrumbTrail(path, leafName);
  return trail.map((crumb, index) => ({
    name: crumb.name,
    url: index === trail.length - 1 ? path : (crumb.href ?? path),
  }));
}

function hindiPagePath(url: string): string | null {
  const path = url.replace(/^https?:\/\/[^/]+/, "");
  return path === "/hi" || path.startsWith("/hi/") ? path : null;
}

/**
 * BreadcrumbList with every item normalized through the canonical URL owner.
 *
 * A HINDI page's list is derived from its path by the visible breadcrumb's own
 * resolver, whatever trail the caller passed. About 35 modules build a
 * BreadcrumbList by hand, and on 200 of 252 Hindi pages the hand-built list
 * disagreed with the visible trail (an English "Home" at `/`, a "Hindi" crumb,
 * English leaf names). Deriving it here makes the two the same by construction.
 * A Hindi page with no auto trail (the /hi/ home, a festival page that renders
 * its own breadcrumb) keeps the caller's trail.
 *
 * The resolver's leaf name wins, so the list says what the visible trail says.
 * Only where the resolver has no Hindi name for the page (it title-cased an
 * English slug) does the caller's Devanagari leaf name stand in, as an edge
 * row's title does in `edgeBreadcrumbTrail`. Before 2026-10 that fallback was
 * missing, and nine /hi/gemstones/ and twelve /hi/vastu-shastra/ pages
 * declared "Blue sapphire" and "Kitchen" over the "नीलम" and "रसोई वास्तु"
 * their callers passed.
 */
export function breadcrumbSchema(trail: BreadcrumbEntry[]) {
  const leaf = trail.at(-1);
  const hindiPath = leaf ? hindiPagePath(leaf.url) : null;
  if (leaf && hindiPath && resolveTrail(hindiPath)) {
    const derived = pathBreadcrumbEntries(hindiPath);
    const derivedLeaf = derived.at(-1)!.name;
    if (!DEVANAGARI.test(derivedLeaf) && DEVANAGARI.test(leaf.name)) {
      return breadcrumbListSchema(pathBreadcrumbEntries(hindiPath, leaf.name));
    }
    return breadcrumbListSchema(derived);
  }
  return breadcrumbListSchema(trail);
}

const DEVANAGARI = /[ऀ-ॿ]/;

/** @public scripts/check-google-seo-standards.mjs requires this helper by name. */
export function buildNestedBreadcrumbs(segments: Array<{ name: string; url: string }>) {
  return breadcrumbSchema(segments);
}

/** @internal */
export const CORE_PILLAR_HUBS: Array<{ name: string; path: string }> = [
  { name: "Free Janam Kundali", path: "/free-kundali/" },
  { name: "Kundli Matching & Milan", path: "/kundli-matching/" },
  { name: "Today Panchang & Tithi", path: "/panchang/" },
  { name: "Choghadiya Muhurat", path: "/choghadiya/" },
  { name: "Aaj Ka Rashifal", path: "/aaj-ka-rashifal/" },
  { name: "Shubh Muhurat Timings", path: "/shubh-muhurat/" },
];

export function websiteSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "@id": `${SITE_URL}#website`,
    url: SITE_URL,
    name: SITE_NAME,
    alternateName: "Ishvaram AI Vedic Astrology",
    publisher: { "@id": `${SITE_URL}#org` },
    inLanguage: SITE_LANGUAGES,
    hasPart: CORE_PILLAR_HUBS.map((hub) => ({
      "@type": "WebPage",
      "@id": `${SITE_URL}${hub.path}`,
      url: `${SITE_URL}${hub.path}`,
      name: hub.name,
      isPartOf: { "@id": `${SITE_URL}#website` },
    })),
    potentialAction: {
      "@type": "SearchAction",
      target: {
        "@type": "EntryPoint",
        urlTemplate: `${SITE_URL}/search/?q={search_term_string}`,
      },
      "query-input": "required name=search_term_string",
    },
  };
}

/**
 * The page language the schema may declare, from the ROUTE first.
 *
 * A `/hi/` URL is a Hindi page whatever the caller passed: until 2026-09-24
 * several Hindi routes reused an English builder (or omitted the field and
 * took the `"en"` default), so Hindi pages declared `inLanguage: "en"`. The
 * route is the one fact that cannot drift from the rendered document. Other
 * URLs keep the caller's value (a Hindi-language page may live at an
 * unprefixed slug), defaulting to English. A regional edition
 * (`/bn/free-kundali/`) declares its own language the same way.
 */
export function schemaLanguage(url: string | undefined, declared: "en" | "hi" | undefined): string {
  if (url) {
    const path = url.startsWith(SITE_URL) ? url.slice(SITE_URL.length) : url;
    if (path === "/hi" || path.startsWith("/hi/")) return "hi";
    const regional = detectRegionalLocale(path);
    if (regional) return regionalLocaleLang[regional];
  }
  return declared ?? "en";
}

export interface CollectionPageInput {
  name: string;
  description: string;
  url: string;
  inLanguage?: "en" | "hi";
}

/** CollectionPage schema — for directories/catalogs, never a single-person profile. */
export function collectionPageSchema(input: CollectionPageInput) {
  return {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: input.name,
    description: input.description,
    url: canonicalItemUrl(input.url),
    inLanguage: schemaLanguage(input.url, input.inLanguage),
    isPartOf: { "@id": `${SITE_URL}#website` },
    publisher: { "@id": `${SITE_URL}#org` },
  };
}

export interface ArticleInput {
  headline: string;
  description: string;
  url?: string;
  datePublished?: string;
  dateModified?: string;
  image?: string;
  inLanguage?: "en" | "hi";
  about?: Array<Record<string, unknown>>;
  /**
   * Completed page-level human-review evidence. Never set this from the global
   * reviewer profile alone; the values must match a published ledger entry
   * (`boundReviewEvidence` in content-reviewer.ts is the one predicate).
   */
  reviewEvidence?: ReviewEvidence;
}

/**
 * Same-day surfaces: their `dateModified` is the ENGINE's IST civil date for
 * today's content (rule 64 same-day contract), which the copy-freshness
 * registry must never replace — `/rashifal/` is registered for its evergreen
 * copy, yet its live gate asserts `"dateModified":"<today>"` daily. The two
 * rashifal hubs come from the one same-day owner the live guard, exporter and
 * edge worker share; the rest are the daily engine routes that pass today's
 * date the same way.
 */
const SAME_DAY_ROUTE_PATHS: ReadonlySet<string> = new Set([
  "/today-horoscope/",
  "/tomorrow-horoscope/",
  "/panchang/today/",
  "/choghadiya/today/",
  "/rahu-kaal/today/",
  // The tomorrow views: rendered today (IST) for IST today + 1.
  ...["panchang", "choghadiya", "rahu-kaal"].flatMap((tool) => [
    `/${tool}/tomorrow/`,
    `/hi/${tool}/tomorrow/`,
  ]),
]);

function isSameDaySurface(pathname: string): boolean {
  return isDailyRashifalHubPath(pathname) || SAME_DAY_ROUTE_PATHS.has(pathname);
}

/**
 * Rule 48 date truth: when the route is registered in the git-derived
 * `PAGE_FRESHNESS` projection, that evidence date IS the Article
 * `dateModified` — a caller-supplied literal can never override it, and a
 * registered page's schema date and visible note cannot drift. Unregistered
 * routes, and same-day surfaces whose date is the engine's, fall back to
 * whatever the caller supplied (or nothing).
 */
export function freshnessDateModified(url: string | undefined): string | undefined {
  if (!url?.startsWith(SITE_URL)) return undefined;
  const route = url.slice(SITE_URL.length).replace(/\/+$/, "") || "/";
  if (isSameDaySurface(`${route}/`)) return undefined;
  // Shared resolver: dynamic families (festival, muhurat) read their family
  // key, so a detail page and its sitemap row cannot disagree (rule 48).
  return (PAGE_FRESHNESS as Record<string, string>)[freshnessKey(route)];
}

/** Evidence dates only: publication as supplied, modification from the freshness owner. */
function articleDates(input: ArticleInput) {
  const dateModified = freshnessDateModified(input.url) ?? input.dateModified;
  return {
    ...(input.datePublished && { datePublished: input.datePublished }),
    ...(dateModified && { dateModified }),
  };
}

/** The page-level review claim — present only for a ledger-bound record. */
function articleReviewClaim(input: ArticleInput) {
  const boundReview = activeContentReviewer() ? boundReviewEvidence(input.reviewEvidence) : null;
  if (!boundReview) return {};
  return {
    reviewedBy: { "@id": CONTENT_REVIEWER_ID },
    dateReviewed: boundReview.reviewedAt.slice(0, 10),
  };
}

/** Article schema — for explanatory / editorial pages. */
export function articleSchema(input: ArticleInput) {
  const url = input.url ? canonicalItemUrl(input.url) : undefined;
  return {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: input.headline,
    description: input.description,
    ...(url && { url, mainEntityOfPage: url }),
    ...(input.image && { image: input.image }),
    ...articleDates(input),
    inLanguage: schemaLanguage(url, input.inLanguage),
    // The publishing Organization, not a person: see `FOUNDER` for why.
    author: ORG_AUTHOR,
    ...(input.about && { about: input.about }),
    // No `speakable`: Google reads that property only for news publishers in
    // Google Assistant. It sat on 485 pages and produced nothing (2026-09-28
    // audit), so no builder emits it (check-google-seo-standards.mjs).
    ...articleReviewClaim(input),
    publisher: { "@id": `${SITE_URL}#org` },
  };
}

/** @internal */
export interface ChartImageObjectInput {
  title: string;
  imageUrl: string;
  caption?: string;
  width?: number;
  height?: number;
  representativeOfPage?: boolean;
}

/**
 * Schema.org ImageObject for visual charts, Kundali diagrams, and Google Images rich results.
 * @public scripts/check-google-seo-standards.mjs requires this helper by name.
 */
export function chartImageObjectSchema(input: ChartImageObjectInput) {
  const fullUrl = input.imageUrl.startsWith("http")
    ? input.imageUrl
    : `${SITE_URL}${input.imageUrl.startsWith("/") ? "" : "/"}${input.imageUrl}`;
  return {
    "@context": "https://schema.org",
    "@type": "ImageObject",
    contentUrl: fullUrl,
    url: fullUrl,
    name: input.title,
    ...(input.caption && { caption: input.caption }),
    ...(input.width && { width: input.width }),
    ...(input.height && { height: input.height }),
    ...(input.representativeOfPage !== undefined && {
      representativeOfPage: input.representativeOfPage,
    }),
  };
}

export interface ToolWebApplicationInput {
  name: string;
  description: string;
  url: string;
  /** One of Google's SoftwareApplication categories; tools default to LifestyleApplication. */
  applicationCategory?: string;
  operatingSystem?: string;
  inLanguage?: "en" | "hi";
  datePublished?: string;
  dateModified?: string;
  /** A sample of the tool's output (e.g. a rendered chart), absolute URL. */
  image?: string;
}

/**
 * Schema.org WebApplication for an interactive calculator or tool page.
 *
 * A tool page is described by this node, not by an Article: the page's main
 * thing is a free calculator, and typing ~70 form pages as editorial articles
 * misdescribed them (2026-09-28 audit). The node carries everything the
 * Article did that is still true of the page — its language from the route,
 * and the same evidence dates (`articleDates`: modification from the
 * freshness owner) — so the date contracts hold on the new node.
 *
 * No `potentialAction`: the "calculate" action this used to emit is not a
 * schema.org type, so validators read it as an unknown entity.
 */
export function toolWebApplicationSchema(input: ToolWebApplicationInput) {
  const url = canonicalItemUrl(
    input.url.startsWith("http")
      ? input.url
      : `${SITE_URL}${input.url.startsWith("/") ? "" : "/"}${input.url}`
  );
  return {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    "@id": `${url}#webapp`,
    name: input.name,
    description: input.description,
    url,
    applicationCategory: input.applicationCategory ?? "LifestyleApplication",
    operatingSystem: input.operatingSystem ?? "All",
    browserRequirements: "Requires JavaScript. Requires HTML5.",
    inLanguage: schemaLanguage(url, input.inLanguage),
    ...(input.image && { image: input.image }),
    ...articleDates({ ...input, headline: input.name, url }),
    isPartOf: { "@id": `${SITE_URL}#website` },
    offers: {
      "@type": "Offer",
      price: "0",
      priceCurrency: "INR",
      availability: "https://schema.org/InStock",
    },
    publisher: { "@id": `${SITE_URL}#org` },
  };
}

export interface WebPageInput {
  name: string;
  description: string;
  url: string;
  inLanguage?: "en" | "hi";
  datePublished?: string;
  dateModified?: string;
}

/**
 * Schema.org WebPage for a page that is neither an article nor a calculator —
 * the AI-chat and consultation landings, whose main content is a chat entry
 * point and an offer. Same evidence dates and publisher as `articleSchema`.
 */
export function webPageSchema(input: WebPageInput) {
  const url = canonicalItemUrl(input.url);
  return {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "@id": `${url}#webpage`,
    name: input.name,
    description: input.description,
    url,
    inLanguage: schemaLanguage(url, input.inLanguage),
    ...articleDates({ ...input, headline: input.name, url }),
    isPartOf: { "@id": `${SITE_URL}#website` },
    publisher: { "@id": `${SITE_URL}#org` },
  };
}

export interface FAQItem {
  question: string;
  answer: string;
}

// No FAQPage builder (2026-10-25): FAQ rich results are retired for this site
// and scaled FAQ markup is a low-value signal while the 2026-07-22 demotion is
// in force, so FAQs stay visible UI only. check-structured-data-coverage.mjs
// rejects any page that reintroduces the type.

// No HowTo builder: Google retired the HowTo rich result and an audit found
// HowTo steps that were not visible on the page, which breaks the
// structured-data content-match policy. Step lists stay as on-page UI only.
